import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlay } from '@fortawesome/free-solid-svg-icons';

const VIDEO_PLACEHOLDER = '/img/ui/shopeevideo.png';

/**
 * VideoCard — thumbnail card for a single Shopee Video entry.
 * Clicking the card opens the video (new tab).
 * Clicking a product thumbnail opens that product link (new tab), stops propagation.
 */
function VideoCard({ video, isAdmin, onDelete, onProductClick, onVideoClick }) {
    const title = video.productName || video.caption || 'Video Shopee';
    const products = video.products || [];
    const visibleProducts = products.slice(0, 3);
    const extraCount = products.length - visibleProducts.length;

    const handleCardClick = () => {
        onVideoClick(video);
    };

    return (
        <div
            onClick={handleCardClick}
            className="bg-surface rounded-xl overflow-hidden shadow-sm border border-line cursor-pointer hover:shadow-md transition-shadow flex flex-col"
        >
            {/* Thumbnail — 3:4 aspect ratio */}
            <div className="relative w-full" style={{ paddingTop: '133%' }}>
                <img
                    src={video.thumbnail || VIDEO_PLACEHOLDER}
                    alt={title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = VIDEO_PLACEHOLDER;
                    }}
                />
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-surface/80 flex items-center justify-center shadow">
                        <FontAwesomeIcon icon={faPlay} className="text-orange-500 text-sm ml-0.5" />
                    </div>
                </div>
                {/* Admin delete */}
                {isAdmin && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(video.id);
                        }}
                        className="absolute top-1 right-1 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        title="Xóa video"
                        aria-label="Xóa video"
                    >
                        <FontAwesomeIcon icon={faTrash} size="xs" />
                    </button>
                )}
            </div>

            {/* Info */}
            <div className="p-2 flex flex-col gap-1 flex-1">
                <p className="text-xs font-semibold text-content line-clamp-2 leading-tight">
                    {title}
                </p>
                {video.author && (
                    <p className="text-[10px] text-content-3 truncate">@{video.author}</p>
                )}

                {/* Product thumbnails row */}
                {visibleProducts.length > 0 && (
                    <div
                        className="flex gap-1 items-center mt-auto pt-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {visibleProducts.map((product) => (
                            <button
                                key={product.id}
                                onClick={() => onProductClick(product)}
                                className="w-8 h-8 rounded-md overflow-hidden border border-line flex-shrink-0 hover:opacity-80 transition-opacity"
                                title={product.productName}
                                aria-label={`Xem sản phẩm: ${product.productName}`}
                            >
                                <img
                                    src={product.imageUrl || VIDEO_PLACEHOLDER}
                                    alt={product.productName}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = VIDEO_PLACEHOLDER;
                                    }}
                                />
                            </button>
                        ))}
                        {extraCount > 0 && (
                            <span className="text-[10px] text-content-3 font-medium whitespace-nowrap">
                                +{extraCount} SP
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default VideoCard;
